/**
 * Opening the camera and microphone, and saying why it did not work.
 *
 * "No camera or microphone could be opened" was the whole of the first
 * client's answer, and it is the wrong answer to every one of the things that
 * actually go wrong (calls-v1 §8): the Space's policy, the browser's
 * permission, the operating system's, a device that is not there, and one
 * another app is holding. Each has a different fix, and only the person in
 * front of the screen can apply it.
 */

export type MediaErrorKind = 'policy' | 'browser' | 'system' | 'missing' | 'busy' | 'unknown'
export type DisplayErrorKind = 'cancelled' | 'policy' | 'system' | 'failed'

/** The page's Permissions-Policy, where the browser will say what it is. */
export interface FeaturePolicy {
  allowsFeature(feature: string): boolean
}

export function pagePolicy(): FeaturePolicy | null {
  if (typeof document === 'undefined') return null
  const withPolicy = document as Document & { permissionsPolicy?: FeaturePolicy; featurePolicy?: FeaturePolicy }
  return withPolicy.permissionsPolicy ?? withPolicy.featurePolicy ?? null
}

interface DeviceChoice {
  audioDeviceId?: string
  videoDeviceId?: string
}

export function mediaConstraints(devices: DeviceChoice, video: boolean): MediaStreamConstraints {
  return {
    audio: devices.audioDeviceId === undefined ? true : { deviceId: { exact: devices.audioDeviceId } },
    video: !video
      ? false
      : devices.videoDeviceId === undefined
        ? true
        : { deviceId: { exact: devices.videoDeviceId } },
  }
}

function errorName(error: unknown): string {
  const name = typeof error === 'object' && error !== null ? (error as { name?: unknown }).name : undefined
  return typeof name === 'string' ? name : ''
}

function errorMessage(error: unknown): string {
  const message = typeof error === 'object' && error !== null ? (error as { message?: unknown }).message : undefined
  return typeof message === 'string' ? message : ''
}

export function classifyMediaError(error: unknown, policy: FeaturePolicy | null, video: boolean): MediaErrorKind {
  const name = errorName(error)
  if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') {
    const features = video ? ['camera', 'microphone'] : ['microphone']
    if (policy !== null && features.some((feature) => !policy.allowsFeature(feature))) return 'policy'
    // Chrome words it so when macOS refused rather than the person.
    if (/system/i.test(errorMessage(error))) return 'system'
    return 'browser'
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') return 'missing'
  if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') return 'busy'
  return 'unknown'
}

export function mediaErrorMessage(kind: MediaErrorKind, video: boolean): string {
  const devices = video ? 'the camera and microphone' : 'the microphone'
  const them = video ? 'them' : 'it'
  switch (kind) {
    case 'policy':
      return `This Space does not allow ${devices} to be used. Its administrator has to change that.`
    case 'browser':
      return `Your browser is blocking ${devices} for this Space. Allow ${them} in the address bar, then try again.`
    case 'system':
      return `Your computer is not letting this browser use ${devices}. Allow it in System Settings, under Privacy & Security, then try again.`
    case 'missing':
      return video ? 'No camera was found. You can join with audio only.' : 'No microphone was found. Connect one, then try again.'
    case 'busy':
      return `Another app is using ${devices}. Close it, then try again.`
    case 'unknown':
      return video ? 'No camera or microphone could be opened.' : 'No microphone could be opened.'
  }
}

export interface LocalMedia {
  stream: MediaStream
  /** Remembered devices that no longer answered, and were opened without. */
  forgot: { audio: boolean; video: boolean }
  /** The camera was asked for and is not there: this is audio alone. */
  audioOnly: boolean
}

/**
 * Opens the devices asked for, and what is there when that fails for a reason
 * the person would only have to undo: a remembered device that has been
 * unplugged is retried as the default, and — where the caller allows it — a
 * camera that is not there is left out. Anything else is thrown, to be
 * classified.
 */
export async function openLocalMedia(
  getUserMedia: (constraints: MediaStreamConstraints) => Promise<MediaStream>,
  request: DeviceChoice & { video: boolean },
  allowAudioOnly: boolean,
): Promise<LocalMedia> {
  const forgot = { audio: false, video: false }
  let audioDeviceId = request.audioDeviceId
  let videoDeviceId = request.videoDeviceId
  let video = request.video
  let audioOnly = false

  // At most three tries: as asked, without a device that has gone, and without the camera.
  for (;;) {
    try {
      const stream = await getUserMedia(mediaConstraints({ audioDeviceId, videoDeviceId }, video))
      return { stream, forgot, audioOnly }
    } catch (caught) {
      const name = errorName(caught)
      if (name === 'OverconstrainedError' && (audioDeviceId !== undefined || videoDeviceId !== undefined)) {
        forgot.audio = forgot.audio || audioDeviceId !== undefined
        forgot.video = forgot.video || videoDeviceId !== undefined
        audioDeviceId = undefined
        videoDeviceId = undefined
        continue
      }
      if (name === 'NotFoundError' && video && allowAudioOnly) {
        video = false
        audioOnly = true
        continue
      }
      throw caught
    }
  }
}

/**
 * Remembered devices the browser no longer lists. An empty list proves
 * nothing: before permission is given, a browser may list no devices at all.
 */
export function missingDevices(
  devices: readonly MediaDeviceInfo[],
  choice: DeviceChoice,
): { audio: boolean; video: boolean } {
  if (devices.length === 0) return { audio: false, video: false }
  const listed = (kind: MediaDeviceKind, id: string | undefined): boolean =>
    id === undefined || devices.some((device) => device.kind === kind && device.deviceId === id)
  return { audio: !listed('audioinput', choice.audioDeviceId), video: !listed('videoinput', choice.videoDeviceId) }
}

export function classifyDisplayError(error: unknown, policy: FeaturePolicy | null): DisplayErrorKind {
  if (errorName(error) !== 'NotAllowedError') return 'failed'
  if (policy !== null && !policy.allowsFeature('display-capture')) return 'policy'
  if (/system/i.test(errorMessage(error))) return 'system'
  return 'cancelled'
}

export function displayErrorMessage(kind: DisplayErrorKind): string | null {
  switch (kind) {
    case 'cancelled':
      return null
    case 'policy':
      return 'This Space does not allow screen sharing. Its administrator has to change that.'
    case 'system':
      return 'Your computer is not letting this browser record the screen. Allow it in System Settings, under Privacy & Security, then try again.'
    case 'failed':
      return 'That screen could not be captured.'
  }
}
